import { registerGuestSessionId } from "./guestIntent";
import { syncWizardFromFeedMatch } from "./resolveMatchIntent";

const SESSION_TOKEN_KEY = "homeChatMatchSessionId";
const CACHE_KEY = "homeChatMatchCache";
const DISMISSED_KEY = "homeChatMatchDismissed";
const MESSAGES_KEY = "homeChatMatchMessages";
const MATCH_SESSION_QUEUE_KEY = "homeChatMatchSessionQueue";
/** Next discovery turn must not reuse the on-screen shortlist's intent. */
const FRESH_THREAD_KEY = "homeChatFreshThread";
/** Keep chat for the latest N match sessions; drop older ones to bound API cost. */
const MAX_RETAINED_MATCH_CHAT_SESSIONS = 2;
const WIZARD_ANSWERS_KEY = "wizardAnswers";
const MATCH_PROFILE_KEY = "matchProfile";
const WIZARD_RESULTS_ACTIVE_KEY = "wizardResultsActive";

const getProductIds = (products = []) =>
  products.map((p) => String(p._id || p.id)).sort().join(",");

/** Stable key for effect dependencies — avoids loops from new object refs. */
export function getAiSessionDependencyKey(aiSession) {
  if (!aiSession?.products?.length) return "";
  return [
    aiSession.sessionId || "",
    getProductIds(aiSession.products),
    JSON.stringify(aiSession.searchParams || null),
  ].join("|");
}

const buildCacheFingerprint = ({
  sessionId,
  products,
  searchParams,
  matchProfile,
  resultKey,
  ownerUserId,
  choice = null,
}) =>
  JSON.stringify({
    sessionId: sessionId || null,
    productIds: getProductIds(products),
    searchParams: searchParams || null,
    matchProfile: matchProfile || null,
    resultKey: resultKey || null,
    ownerUserId: ownerUserId ?? null,
    choice: choice || null,
  });

const readRawHomeChatMatchCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Whether cached match data belongs to the current auth context. */
export function isCacheOwnerValid(cache, userId = null) {
  if (!cache) return false;

  const currentOwner = userId ?? null;
  const cacheOwner =
    cache.ownerUserId === undefined ? null : (cache.ownerUserId ?? null);

  // Legacy caches without ownerUserId are guest-only.
  if (cache.ownerUserId === undefined) {
    return currentOwner === null;
  }

  return cacheOwner === currentOwner;
}

const normalizeCachedMatch = (parsed) => ({
  ...parsed,
  products: normalizeChatMatchProducts(
    parsed.products,
    parsed.searchParams,
    parsed.matchProfile,
  ),
});

/**
 * Read home chat match cache scoped to the current user (null = guest).
 */
export function readHomeChatMatchCache(userId = null) {
  try {
    const parsed = readRawHomeChatMatchCache();
    if (!parsed?.products?.length) return null;
    if (!isCacheOwnerValid(parsed, userId)) return null;
    return normalizeCachedMatch(parsed);
  } catch {
    return null;
  }
}

export function readHomeChatMatchSessionId() {
  try {
    return (
      localStorage.getItem(SESSION_TOKEN_KEY) ||
      localStorage.getItem("currentSessionId") ||
      null
    );
  } catch {
    return null;
  }
}

/** Product IDs dismissed for the current match session (survives refresh). */
export function readDismissedMatchProductIds() {
  try {
    const sessionId = readHomeChatMatchSessionId();
    if (!sessionId) return [];
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.sessionId !== sessionId) return [];
    return Array.isArray(parsed.productIds)
      ? parsed.productIds.map(String)
      : [];
  } catch {
    return [];
  }
}

export function addDismissedMatchProductId(productId) {
  const id = productId != null ? String(productId) : "";
  if (!id) return readDismissedMatchProductIds();

  try {
    const sessionId = readHomeChatMatchSessionId();
    if (!sessionId) return [];

    const existing = readDismissedMatchProductIds();
    if (existing.includes(id)) return existing;

    const productIds = [...existing, id];
    localStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify({ sessionId, productIds }),
    );
    window.dispatchEvent(new CustomEvent("homeChatMatchDismissed"));
    return productIds;
  } catch {
    return readDismissedMatchProductIds();
  }
}

export function clearDismissedMatchProductIds() {
  try {
    localStorage.removeItem(DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

function normalizePersistedMessage(m) {
  if (!m || (m.role !== "user" && m.role !== "assistant") || !m.content) {
    return null;
  }
  return {
    role: m.role,
    content: String(m.content).slice(0, 2000),
    ...(m.isError ? { isError: true } : {}),
    ...(m.matchSessionId ? { matchSessionId: String(m.matchSessionId) } : {}),
  };
}

function readMatchSessionQueue() {
  try {
    const raw = localStorage.getItem(MATCH_SESSION_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map(String).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

function persistMatchSessionQueue(queue = []) {
  try {
    localStorage.setItem(
      MATCH_SESSION_QUEUE_KEY,
      JSON.stringify(queue.map(String).filter(Boolean)),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Tag the open transcript to this match session. When a 3rd distinct match
 * session arrives, drop messages from the oldest so only the last two
 * sessions' chats remain (user can still refine the latest shortlist).
 */
export function pruneHomeChatMessagesForMatchSession(sessionId, messages = []) {
  if (!sessionId || !Array.isArray(messages)) {
    return Array.isArray(messages) ? messages : [];
  }

  const sid = String(sessionId);
  const tagged = messages
    .map(normalizePersistedMessage)
    .filter(Boolean)
    .map((m) => (m.matchSessionId ? m : { ...m, matchSessionId: sid }));

  let queue = readMatchSessionQueue().filter((id) => id !== sid);
  queue.push(sid);

  const dropped = new Set();
  if (queue.length > MAX_RETAINED_MATCH_CHAT_SESSIONS) {
    queue
      .slice(0, queue.length - MAX_RETAINED_MATCH_CHAT_SESSIONS)
      .forEach((id) => dropped.add(id));
    queue = queue.slice(-MAX_RETAINED_MATCH_CHAT_SESSIONS);
  }
  persistMatchSessionQueue(queue);

  if (!dropped.size) return tagged;

  return tagged.filter(
    (m) => !m.matchSessionId || !dropped.has(m.matchSessionId),
  );
}

/** Discovery chat transcript for the current match thread (survives collapse/refresh). */
export function readHomeChatMessages() {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.messages) || !parsed.messages.length) return null;
    return parsed.messages.map(normalizePersistedMessage).filter(Boolean).slice(-40);
  } catch {
    return null;
  }
}

export function persistHomeChatMessages(messages = []) {
  if (!Array.isArray(messages) || !messages.length) return false;
  try {
    localStorage.setItem(
      MESSAGES_KEY,
      JSON.stringify({
        messages: messages
          .map(normalizePersistedMessage)
          .filter(Boolean)
          .slice(-40),
        savedAt: new Date().toISOString(),
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearHomeChatMessages() {
  try {
    localStorage.removeItem(MESSAGES_KEY);
    localStorage.removeItem(MATCH_SESSION_QUEUE_KEY);
  } catch {
    /* ignore */
  }
}

/** True after Clear chat, until the next shortlist replaces the old intent. */
export function isHomeChatFreshThread() {
  try {
    return localStorage.getItem(FRESH_THREAD_KEY) === "1";
  } catch {
    return false;
  }
}

export function markHomeChatFreshThread() {
  try {
    localStorage.setItem(FRESH_THREAD_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearHomeChatFreshThread() {
  try {
    localStorage.removeItem(FRESH_THREAD_KEY);
  } catch {
    /* ignore */
  }
}

const normalizeChatMatchProducts = (
  products,
  searchParams = null,
  matchProfile = null,
) => {
  const fallbackCategory =
    products.find((p) => p?.category)?.category ||
    (matchProfile?.category?._id || matchProfile?.category?.name
      ? {
          _id: matchProfile.category._id || matchProfile.category.id,
          name: matchProfile.category.name || matchProfile.category,
        }
      : null);

  return products.map((product, index) => ({
    ...product,
    category: product.category || fallbackCategory || undefined,
    rank: product.rank ?? index + 1,
    aiRanked: product.aiRanked !== false,
  }));
};

/** Mirror server active feed match into browser cache for PDP / cross-page use. */
export function syncActiveFeedMatchToLocalCache(match, ownerUserId = null) {
  if (!match?.products?.length) return false;
  return persistHomeChatMatch({
    sessionId: match.sessionId,
    products: match.products,
    searchParams: match.searchParams ?? null,
    matchProfile: match.matchProfile ?? null,
    ownerUserId,
    choice: Object.prototype.hasOwnProperty.call(match, "choice")
      ? match.choice
      : undefined,
  });
}

export function persistHomeChatMatch({
  sessionId,
  products,
  searchParams,
  matchProfile,
  resultKey = null,
  ownerUserId = null,
  choice,
}) {
  if (!products?.length) return false;

  const normalizedProducts = normalizeChatMatchProducts(
    products,
    searchParams,
    matchProfile,
  );

  const resolvedSessionId = sessionId || readHomeChatMatchSessionId();
  const resolvedOwnerUserId = ownerUserId ?? null;

  let parsed = null;
  try {
    parsed = readRawHomeChatMatchCache();
  } catch {
    parsed = null;
  }

  const sameSession = Boolean(
    parsed?.sessionId &&
      resolvedSessionId &&
      parsed.sessionId === resolvedSessionId,
  );
  const nextChoice =
    choice === null
      ? null
      : choice
        ? choice
        : sameSession
          ? parsed?.choice || null
          : null;

  const nextFingerprint = buildCacheFingerprint({
    sessionId: resolvedSessionId,
    products: normalizedProducts,
    searchParams,
    matchProfile,
    resultKey,
    ownerUserId: resolvedOwnerUserId,
    choice: nextChoice,
  });

  try {
    if (parsed) {
      const existingFingerprint = buildCacheFingerprint({
        sessionId: parsed.sessionId,
        products: parsed.products,
        searchParams: parsed.searchParams,
        matchProfile: parsed.matchProfile,
        resultKey: parsed.resultKey,
        ownerUserId: parsed.ownerUserId ?? null,
        choice: parsed.choice || null,
      });
      if (existingFingerprint === nextFingerprint) {
        return false;
      }
    }

    if (sessionId) {
      localStorage.setItem(SESSION_TOKEN_KEY, sessionId);
      localStorage.setItem("currentSessionId", sessionId);
      registerGuestSessionId(sessionId);
    }

    // New match session → dismissals from a previous shortlist no longer apply.
    const previousSessionId = parsed?.sessionId || null;
    if (
      resolvedSessionId &&
      previousSessionId &&
      previousSessionId !== resolvedSessionId
    ) {
      clearDismissedMatchProductIds();
    }

    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        sessionId: resolvedSessionId,
        products: normalizedProducts,
        searchParams: searchParams || null,
        matchProfile: matchProfile || null,
        savedAt: new Date().toISOString(),
        resultKey: resultKey || null,
        ownerUserId: resolvedOwnerUserId,
        choice: nextChoice,
      }),
    );

    window.dispatchEvent(new CustomEvent("homeChatMatchUpdated"));
    syncWizardFromFeedMatch({ searchParams, matchProfile });
    return true;
  } catch {
    /* quota / private mode */
    return false;
  }
}

/**
 * Returns the cached result if its resultKey matches the given key.
 * Used to validate that a stored cache entry still represents the same
 * inputs before showing it to the user.
 */
export function readHomeChatMatchByResultKey(key, userId = null) {
  if (!key) return null;
  try {
    const cached = readHomeChatMatchCache(userId);
    if (!cached?.resultKey || cached.resultKey !== key) return null;
    return cached;
  } catch {
    return null;
  }
}

export function clearHomeChatMatch() {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(CACHE_KEY);
    clearDismissedMatchProductIds();
    clearHomeChatMessages();
    clearHomeChatFreshThread();
  } catch {
    /* ignore */
  }
}

/** Clear all browser-stored match intent (home cache + wizard answers). */
export function clearMatchIntentStorage() {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(CACHE_KEY);
    clearDismissedMatchProductIds();
    clearHomeChatMessages();
    clearHomeChatFreshThread();
    localStorage.removeItem(WIZARD_ANSWERS_KEY);
    localStorage.removeItem(MATCH_PROFILE_KEY);
    localStorage.removeItem(WIZARD_RESULTS_ACTIVE_KEY);
    window.dispatchEvent(new CustomEvent("homeChatMatchUpdated"));
  } catch {
    /* ignore */
  }
}

/**
 * Drop stale match intent after auth resolves (e.g. page refresh while logged in).
 */
export function reconcileMatchIntentStorageForUser(userId = null) {
  try {
    const raw = readRawHomeChatMatchCache();
    const current = userId ?? null;

    if (raw && !isCacheOwnerValid(raw, current)) {
      clearMatchIntentStorage();
      return;
    }

    if (current && !raw) {
      if (
        localStorage.getItem(WIZARD_ANSWERS_KEY) ||
        localStorage.getItem(MATCH_PROFILE_KEY)
      ) {
        clearMatchIntentStorage();
      }
      return;
    }

    if (!current && raw?.ownerUserId) {
      clearMatchIntentStorage();
    }
  } catch {
    /* ignore */
  }
}

/**
 * Clear match intent when the authenticated user changes mid-session.
 * Preserves guest intent on guest → login so mergeGuestIntentAfterAuth can run.
 */
export function handleAuthUserChange(previousUserId, nextUserId) {
  const prev = previousUserId ?? null;
  const next = nextUserId ?? null;
  if (prev === next) return;

  // First auth resolution — reconcileMatchIntentStorageForUser handles refresh.
  if (previousUserId === undefined) return;

  // Guest signing in — keep local intent for server merge.
  if (prev === null && next !== null) return;

  clearMatchIntentStorage();
}
